/**
 * PremiumCPFCNPJInput.jsx
 * Input de CPF/CNPJ com validação completa
 *
 * Detecta automaticamente:
 * - CPF: 000.000.000-00 (11 dígitos)
 * - CNPJ: 00.000.000/0001-00 (14 dígitos)
 *
 * Validações:
 * - Dígitos verificadores (algoritmo oficial)
 * - Números repetidos (111.111.111-11 inválido)
 */

import React, { useState, useCallback, forwardRef } from 'react';
import { InputAdornment } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PremiumInput from './PremiumInput';

/**
 * Formata CPF ou CNPJ automaticamente
 */
const formatCPFCNPJ = (value) => {
  if (!value) return '';

  const clean = value.replace(/\D/g, '');

  if (clean.length === 0) return '';

  // CPF: 000.000.000-00
  if (clean.length <= 11) {
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
    if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
    return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`;
  }

  // CNPJ: 00.000.000/0001-00
  const cnpj = clean.slice(0, 14);
  if (cnpj.length <= 2) return cnpj;
  if (cnpj.length <= 5) return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
  if (cnpj.length <= 8) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
  if (cnpj.length <= 12) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
};

/**
 * Valida CPF (algoritmo oficial)
 */
const validateCPF = (cpf) => {
  const clean = cpf.replace(/\D/g, '');

  if (clean.length !== 11) return false;

  // Rejeita CPFs com todos os dígitos iguais
  if (/^(\d)\1+$/.test(clean)) return false;

  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(clean[i], 10) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  if (digit !== parseInt(clean[9], 10)) return false;

  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(clean[i], 10) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit > 9) digit = 0;
  return digit === parseInt(clean[10], 10);
};

/**
 * Valida CNPJ (algoritmo oficial)
 */
const validateCNPJ = (cnpj) => {
  const clean = cnpj.replace(/\D/g, '');

  if (clean.length !== 14) return false;

  // Rejeita CNPJs com todos os dígitos iguais
  if (/^(\d)\1+$/.test(clean)) return false;

  // Função auxiliar para calcular dígitos
  const calcDigit = (digits, weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      sum += parseInt(digits[i], 10) * weights[i];
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  // Pesos para o primeiro dígito
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  // Pesos para o segundo dígito
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const digit1 = calcDigit(clean, weights1);
  const digit2 = calcDigit(clean, weights2);

  return digit1 === parseInt(clean[12], 10) && digit2 === parseInt(clean[13], 10);
};

/**
 * Valida CPF ou CNPJ
 */
const validateCPFCNPJ = (value) => {
  if (!value) return { valid: null, message: '', type: null };

  const clean = value.replace(/\D/g, '');

  if (clean.length === 0) {
    return { valid: null, message: '', type: null };
  }

  // Ainda digitando CPF
  if (clean.length < 11) {
    return { valid: null, message: `${clean.length}/11 dígitos (CPF)`, type: 'cpf' };
  }

  // CPF completo
  if (clean.length === 11) {
    if (validateCPF(clean)) {
      return { valid: true, message: 'CPF válido', type: 'cpf' };
    }
    return { valid: false, message: 'CPF inválido', type: 'cpf' };
  }

  // Digitando CNPJ
  if (clean.length < 14) {
    return { valid: null, message: `${clean.length}/14 dígitos (CNPJ)`, type: 'cnpj' };
  }

  // CNPJ completo
  if (clean.length === 14) {
    if (validateCNPJ(clean)) {
      return { valid: true, message: 'CNPJ válido', type: 'cnpj' };
    }
    return { valid: false, message: 'CNPJ inválido', type: 'cnpj' };
  }

  return { valid: false, message: 'Documento inválido', type: null };
};

/**
 * PremiumCPFCNPJInput Component
 */
const PremiumCPFCNPJInput = forwardRef(({
  value,
  onChange,
  label = 'CPF/CNPJ',
  showIcon = true,
  ...props
}, ref) => {
  const [validation, setValidation] = useState({ valid: null, message: '', type: null });

  const handleChange = useCallback((e) => {
    const formatted = formatCPFCNPJ(e.target.value);
    const result = validateCPFCNPJ(formatted);
    setValidation(result);
    onChange?.({ target: { value: formatted, name: e.target.name } });
  }, [onChange]);

  const getHelperTextColor = () => {
    if (validation.valid === true) return 'success.main';
    if (validation.valid === false) return 'error.main';
    return 'text.secondary';
  };

  const getIcon = () => {
    if (validation.type === 'cnpj') {
      return <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />;
    }
    return <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />;
  };

  return (
    <PremiumInput
      ref={ref}
      {...props}
      value={value || ''}
      onChange={handleChange}
      label={label}
      placeholder="000.000.000-00"
      inputProps={{
        maxLength: 18,
        inputMode: 'numeric',
        ...props.inputProps,
      }}
      InputProps={{
        startAdornment: showIcon ? (
          <InputAdornment position="start">
            {getIcon()}
          </InputAdornment>
        ) : undefined,
        endAdornment: validation.valid === true ? (
          <InputAdornment position="end">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </InputAdornment>
        ) : undefined,
        ...props.InputProps,
      }}
      helperText={validation.message}
      FormHelperTextProps={{
        sx: { color: getHelperTextColor() },
      }}
      error={validation.valid === false}
      success={validation.valid === true}
    />
  );
});

PremiumCPFCNPJInput.displayName = 'PremiumCPFCNPJInput';

export default PremiumCPFCNPJInput;
